/**
 * macOS-style overlay scrollbars for the Web client.
 *
 * Windows Chrome/Electron draws classic always-on scrollbars that claim ~17px
 * of layout; the macOS app rides the system overlay `NSScroller` — a floating
 * capsule that appears on scroll and fades out. This module reproduces that
 * behaviour cross-platform:
 *
 *   1. The wired scroller's native scrollbar is hidden (`.ob-native-hidden`).
 *   2. A capsule thumb (`.ob-thumb`) is appended to a positioned host that
 *      bounds the scroller. It fades in on scroll, fades out
 *      `scrollbar.idleDelayMs` after the last scroll event, and is draggable.
 *
 * Wiring pattern (`useOverlayScrollbar`): the host is a wrapper element that
 * bounds the scroller exactly — React mounts children bottom-up, so either
 * ref completing triggers a re-sync, and the controller positions the thumb
 * from the scroller's offset chain, which also handles nested scrollers
 * (e.g. the source textarea inside `.editor-body`).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { scrollbar } from '../../design/tokens.js';

export type ScrollAxis = 'x' | 'y';

/** Pure thumb geometry so the maths stays unit-testable without a DOM. */
export interface ThumbGeometry {
  /** Capsule length along the axis, px. */
  length: number;
  /** Capsule offset from the track origin, px. */
  offset: number;
}

/**
 * Computes capsule length/offset for a track of `viewportLen` showing
 * `contentLen` of content at `scrollPos`. Mirrors NSScroller knob math:
 * length is proportional to the visible fraction (clamped to
 * `minLength` and to the viewport itself), offset divides the leftover
 * travel by the scroll ratio.
 */
export function computeThumbGeometry(
  viewportLen: number,
  contentLen: number,
  scrollPos: number,
  minLength: number,
): ThumbGeometry {
  const maxScroll = Math.max(0, contentLen - viewportLen);
  const raw = viewportLen * (viewportLen / Math.max(1, contentLen));
  const length = Math.min(viewportLen, Math.max(minLength, Math.round(raw)));
  const travel = Math.max(0, viewportLen - length);
  const clamped = Math.min(Math.max(scrollPos, 0), maxScroll);
  const offset = maxScroll > 0 ? Math.round((clamped / maxScroll) * travel) : 0;
  return { length, offset };
}

/** Sum of offsetLeft/offsetTop up to (excluding) `host`; null if disconnected. */
function offsetInHost(scroller: HTMLElement, host: HTMLElement): { top: number; left: number } | null {
  let top = 0;
  let left = 0;
  let el: HTMLElement | null = scroller;
  while (el && el !== host) {
    top += el.offsetTop;
    left += el.offsetLeft;
    el = el.offsetParent as HTMLElement | null;
  }
  return el === host ? { top, left } : null;
}

export interface OverlayScrollbarOptions {
  /** Which axis the scroller travels on. Default `'y'`. */
  axis?: ScrollAxis;
}

/**
 * Wires one scroller to one host. Idempotent per element pair; returns a
 * dispose function that removes the thumb, listeners and observers.
 */
export function attachOverlayScrollbar(
  scroller: HTMLElement,
  host: HTMLElement,
  options: OverlayScrollbarOptions = {},
): () => void {
  const axis: ScrollAxis = options.axis ?? 'y';
  const vertical = axis === 'y';
  const thumb = document.createElement('div');
  thumb.className = 'ob-thumb';
  thumb.setAttribute('data-axis', axis);
  thumb.setAttribute('aria-hidden', 'true');

  let disposed = false;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;
  let origin = 0;
  let trackLen = 0;
  let thumbLen = 0;
  let maxScroll = 1;

  scroller.classList.add('ob-native-hidden');

  /** React can replace the host node; re-append and re-measure if so. */
  function ensureMounted(): void {
    if (!thumb.isConnected) host.appendChild(thumb);
  }

  function measure(): void {
    const box = offsetInHost(scroller, host);
    if (!box) return;
    origin = vertical ? box.top : box.left;
    trackLen = vertical ? scroller.clientHeight : scroller.clientWidth;
    const contentLen = vertical ? scroller.scrollHeight : scroller.scrollWidth;
    maxScroll = Math.max(0, contentLen - trackLen);
    if (maxScroll <= 1) {
      // Nothing to scroll: keep the capsule out of the way entirely.
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = null;
      thumb.classList.remove('ob-show');
      thumb.style.display = 'none';
      return;
    }
    thumb.style.display = '';
    position();
  }

  function position(): void {
    const scrollPos = vertical ? scroller.scrollTop : scroller.scrollLeft;
    const { length, offset } = computeThumbGeometry(
      trackLen,
      trackLen + maxScroll,
      scrollPos,
      scrollbar.thumbMinLength,
    );
    thumbLen = length;
    if (vertical) {
      thumb.style.height = `${length}px`;
      thumb.style.width = '';
      thumb.style.transform = `translateY(${origin + offset}px)`;
    } else {
      thumb.style.width = `${length}px`;
      thumb.style.height = '';
      thumb.style.transform = `translateX(${origin + offset}px)`;
    }
  }

  function show(): void {
    ensureMounted();
    thumb.classList.add('ob-show');
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (!disposed) thumb.classList.remove('ob-show');
    }, scrollbar.idleDelayMs);
  }

  function onScroll(): void {
    measure();
    show();
  }

  /** Thumb drag: maps pointer travel to scroll via the knob ratio. */
  function onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    thumb.setPointerCapture(event.pointerId);
    thumb.classList.add('ob-drag');
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    const startPos = vertical ? event.clientY : event.clientX;
    const startScroll = vertical ? scroller.scrollTop : scroller.scrollLeft;
    const travel = Math.max(0, trackLen - thumbLen);

    function onMove(move: PointerEvent): void {
      if (travel <= 0 || maxScroll <= 0) return;
      const delta = (vertical ? move.clientY : move.clientX) - startPos;
      const ratio = Math.min(1, Math.max(0, startScroll / maxScroll + delta / travel));
      if (vertical) scroller.scrollTop = ratio * maxScroll;
      else scroller.scrollLeft = ratio * maxScroll;
    }

    function onUp(): void {
      thumb.classList.remove('ob-drag');
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', onUp);
      thumb.removeEventListener('pointercancel', onUp);
      show();
    }

    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', onUp);
    thumb.addEventListener('pointercancel', onUp);
  }

  function onInput(): void {
    // Typing changes scrollHeight line by line; re-measure, don't flash.
    measure();
  }

  scroller.addEventListener('scroll', onScroll, { passive: true });
  thumb.addEventListener('pointerdown', onPointerDown);
  if (scroller instanceof HTMLTextAreaElement || scroller instanceof HTMLInputElement) {
    scroller.addEventListener('input', onInput);
  }
  // Container and content resizes (window resize, sidebar drag, flex reflow)
  // both recompute the track; typing re-measures via the input listener above.
  const observer =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => measure())
      : null;
  observer?.observe(scroller);
  observer?.observe(host);

  measure();

  return function dispose(): void {
    if (disposed) return;
    disposed = true;
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = null;
    scroller.removeEventListener('scroll', onScroll);
    thumb.removeEventListener('pointerdown', onPointerDown);
    scroller.removeEventListener('input', onInput);
    observer?.disconnect();
    scroller.classList.remove('ob-native-hidden');
    thumb.remove();
  };
}

export interface OverlayScrollbarHandle<T extends HTMLElement> {
  /** Ref for the positioned host wrapper that bounds the scroller. */
  hostRef: (node: HTMLElement | null) => void;
  /** Ref for the scrolling element itself. */
  scrollerRef: (node: T | null) => void;
}

/**
 * React binding for `attachOverlayScrollbar`. Both refs are stable and
 * re-sync whenever either lands (children mount before parents, swap freely).
 * Several handles may share one host — only whichever scroller is mounted
 * will ever show its thumb.
 */
export function useOverlayScrollbar<T extends HTMLElement>(
  options?: OverlayScrollbarOptions,
): OverlayScrollbarHandle<T> {
  const host = useRef<HTMLElement | null>(null);
  const scroller = useRef<T | null>(null);
  const dispose = useRef<(() => void) | null>(null);
  const optionsRef = useRef(options);

  const sync = useCallback(() => {
    dispose.current?.();
    dispose.current = null;
    if (host.current && scroller.current) {
      dispose.current = attachOverlayScrollbar(scroller.current, host.current, optionsRef.current);
    }
  }, []);

  const hostRef = useCallback(
    (node: HTMLElement | null) => {
      host.current = node;
      sync();
    },
    [sync],
  );

  const scrollerRef = useCallback(
    (node: T | null) => {
      scroller.current = node;
      sync();
    },
    [sync],
  );

  // Safety net: React does not null refs when an ancestor unmounts the tree.
  useEffect(
    () => () => {
      dispose.current?.();
      dispose.current = null;
    },
    [],
  );

  return useMemo(() => ({ hostRef, scrollerRef }), [hostRef, scrollerRef]);
}
